import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  Badge,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Group as UsersIcon,
  Description as FileTextIcon,
  Public as GlobeIcon,
  CalendarToday as CalendarIcon,
  EmojiEvents as AwardIcon,
  AccessTime as ClockIcon,
  Logout as LogoutIcon,
  Warning as AlertIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Import tab components
import EmployeesTab from "./admin/EmployeesTab";
import PaystubRequestsTab from "./admin/PaystubRequestsTab";
import ImmigrationDocsTab from "./admin/ImmigrationDocsTab";
import LeaveRequestsTab from "./admin/LeaveRequestsTab";
import TimesheetsTab from "./admin/TimesheetsTab";
import MilestonesTab from "./admin/MilestonesTab";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    "aria-controls": `admin-tabpanel-${index}`,
  };
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!user) {
    navigate("/");
    return null;
  }

  // Badge counts for demonstration
  const badgeCounts = {
    paystubs: 3,
    immigration: 5,
    leave: 8,
    timesheets: 12,
  };

  const totalPending = Object.values(badgeCounts).reduce((sum, count) => sum + count, 0);

  const tabs = [
    { label: "Employees", icon: <UsersIcon />, component: <EmployeesTab />, badge: 0 },
    { label: "Paystub Requests", icon: <FileTextIcon />, component: <PaystubRequestsTab />, badge: badgeCounts.paystubs },
    { label: "Immigration Docs", icon: <GlobeIcon />, component: <ImmigrationDocsTab />, badge: badgeCounts.immigration },
    { label: "Leave Requests", icon: <CalendarIcon />, component: <LeaveRequestsTab />, badge: badgeCounts.leave },
    { label: "Timesheets", icon: <ClockIcon />, component: <TimesheetsTab />, badge: badgeCounts.timesheets },
    { label: "Milestones", icon: <AwardIcon />, component: <MilestonesTab />, badge: 0 },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            background: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(10px)",
            borderRadius: theme.spacing(2),
            boxShadow: theme.shadows[10],
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  mb: 1,
                }}
              >
                Employee Management
              </Typography>
              {totalPending > 0 && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AlertIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="body2" color="error" fontWeight={500}>
                    {totalPending} pending requests require attention
                  </Typography>
                </Box>
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
            >
              Logout
            </Button>
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper
          sx={{
            background: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(10px)",
            borderRadius: theme.spacing(2),
            boxShadow: theme.shadows[10],
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="admin dashboard tabs"
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  minHeight: 72,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={
                    tab.badge > 0 ? (
                      <Badge badgeContent={tab.badge} color="error">
                        {tab.icon}
                      </Badge>
                    ) : (
                      tab.icon
                    )
                  }
                  label={tab.label}
                  iconPosition="start"
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <Box sx={{ p: 3 }}>
            {tabs.map((tab, index) => (
              <TabPanel key={index} value={activeTab} index={index}>
                {tab.component}
              </TabPanel>
            ))}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Dashboard;